import { useState } from "react";
import { DndContext, closestCorners, PointerSensor, useSensor, useSensors, useDraggable, useDroppable, DragOverlay } from "@dnd-kit/core";
import { Phone, Mail, MessageSquare } from "lucide-react";

const STATUSES = ["New", "Contacted", "Qualified", "Interested", "Follow-up", "Converted", "Closed", "Lost"];
const COLORS = {
  New: "sky", Contacted: "orange", Qualified: "violet", Interested: "cyan",
  "Follow-up": "amber", Converted: "emerald", Closed: "neutral", Lost: "red"
};

function LeadCard({ lead, dragging = false }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: lead.id, data: { lead } });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined;
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}
         className={`p-3 bg-white rounded-xl border border-neutral-200 shadow-sm cursor-grab active:cursor-grabbing ${(isDragging || dragging) ? "opacity-40 shadow-lg" : ""}`}
         data-testid={`kanban-card-${lead.id}`}>
      <div className="font-semibold text-sm">{lead.name}</div>
      <div className="mt-2 space-y-1 text-xs text-neutral-600">
        <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> {lead.phone}</div>
        {lead.email && <div className="flex items-center gap-1.5"><Mail className="w-3 h-3" /> {lead.email}</div>}
        {lead.course && <div className="mt-1 inline-block px-2 py-0.5 rounded-full bg-neutral-100 text-[10px]">{lead.course}</div>}
      </div>
      <div className="mt-2 text-[10px] text-neutral-400">{lead.source}</div>
    </div>
  );
}

function Column({ status, leads }) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  const color = COLORS[status] || "neutral";
  return (
    <div ref={setNodeRef}
         className={`flex-shrink-0 w-72 rounded-2xl p-3 transition-colors ${isOver ? "bg-sky-100" : "bg-neutral-100"}`}
         data-testid={`kanban-column-${status}`}>
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full bg-${color}-500`}></span>
          <span className="font-display font-bold text-sm">{status}</span>
        </div>
        <span className="text-xs font-semibold text-neutral-500 bg-white rounded-full px-2 py-0.5">{leads.length}</span>
      </div>
      <div className="space-y-2 min-h-[100px]">
        {leads.map((l) => <LeadCard key={l.id} lead={l} />)}
        {leads.length === 0 && <div className="text-xs text-neutral-400 text-center py-4">Drop here</div>}
      </div>
    </div>
  );
}

export default function LeadKanban({ leads, onStatusChange }) {
  const [active, setActive] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const grouped = STATUSES.reduce((acc, s) => {
    acc[s] = leads.filter((l) => (l.status || "New") === s);
    return acc;
  }, {});

  const handleDragEnd = (event) => {
    setActive(null);
    const { active, over } = event;
    if (!over) return;
    const lead = active.data.current?.lead;
    if (lead && STATUSES.includes(over.id) && lead.status !== over.id) {
      onStatusChange(lead.id, over.id);
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners}
                onDragStart={(e) => setActive(e.active.data.current?.lead)}
                onDragEnd={handleDragEnd}>
      <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
        {STATUSES.map((s) => <Column key={s} status={s} leads={grouped[s]} />)}
      </div>
      <DragOverlay>{active && <LeadCard lead={active} dragging />}</DragOverlay>
    </DndContext>
  );
}
